// ABOUTME: Appearance controls for the Customize sheet — columns, spacing, grouping, theme.
// ABOUTME: Extracted from AddToDashboardModal so a single sheet owns every control.

import React, { useState } from "react";
import { useUIStore, type Spacing } from "../../stores/ui";
import {
  ACCENT_PALETTES,
  COLOR_PRESETS,
  DEFAULT_LIGHT_BG,
  DEFAULT_DARK_BG,
  CINEMATIC_PRESETS,
  deriveAccent,
} from "../../utils/adaptiveColor";
import { THEME_PRESETS, type ThemePreset } from "../../constants/themePresets";

const SPACING_OPTIONS: { value: Spacing; label: string }[] = [
  { value: "compact", label: "Compact" },
  { value: "default", label: "Default" },
  { value: "relaxed", label: "Relaxed" },
];

interface AppearanceTabProps {
  columnCount: number;
  onChangeColumns: (count: number) => void;
}

const AppearanceTab: React.FC<AppearanceTabProps> = ({ columnCount, onChangeColumns }) => {
  // App.tsx already re-applies the theme whenever baseColor changes, so this
  // component only needs to set it.
  const {
    baseColor,
    setBaseColor,
    paletteIdx,
    setPaletteIdx,
    spacing,
    setSpacing,
    groupingEnabled,
    setGroupingEnabled,
  } = useUIStore();
  const [customOpen, setCustomOpen] = useState(false);

  const applyPreset = (preset: ThemePreset) => {
    setBaseColor(preset.background);
    setPaletteIdx(ACCENT_PALETTES.findIndex((name) => name === preset.palette));
  };

  // A preset is active only when both its background and its accent match.
  const isPresetActive = (preset: ThemePreset) =>
    baseColor === preset.background && ACCENT_PALETTES[paletteIdx] === preset.palette;

  return (
    <div className="space-y-8">
      {/* Columns */}
      <div>
        <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">Columns</h4>
        <p className="text-sm text-[var(--color-text-tertiary)] mb-4">
          Changing columns redistributes your items automatically.
        </p>
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              aria-label="Decrease columns"
              onClick={() => onChangeColumns(columnCount - 1)}
              className="p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-hover)] transition disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={columnCount <= 2}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                <path d="M6 10h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="flex gap-1">
              {[...Array(Math.max(2, columnCount))].map((_, i) => (
                <div
                  key={i}
                  className={`w-6 h-12 border-2 rounded transition-all ${
                    i < columnCount
                      ? "border-[var(--color-accent)] bg-[var(--color-bg-hover)]"
                      : "border-[var(--color-border)] bg-[var(--color-bg-secondary)]"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              aria-label="Increase columns"
              onClick={() => onChangeColumns(columnCount + 1)}
              className="p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-hover)] transition disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={columnCount >= 5}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                <path
                  d="M10 6v8M6 10h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <div className="text-sm text-[var(--color-text-primary)] font-medium">
            {columnCount} column{columnCount !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Spacing */}
      <div>
        <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">Spacing</h4>
        <p className="text-sm text-[var(--color-text-tertiary)] mb-4">
          Adjust how tightly links are packed on your dashboard.
        </p>
        <div className="flex gap-2">
          {SPACING_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSpacing(opt.value)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all border
                ${
                  spacing === opt.value
                    ? "bg-[var(--color-accent)] text-[var(--color-text-inverse)] border-[var(--color-accent)]"
                    : "bg-[var(--color-bg)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grouping */}
      <div>
        <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">Grouping</h4>
        <p className="text-sm text-[var(--color-text-tertiary)] mb-4">
          Sorts your links under headings taken from the tags already on them.
        </p>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 accent-[var(--color-accent)] cursor-pointer"
            checked={groupingEnabled}
            onChange={(e) => setGroupingEnabled(e.target.checked)}
          />
          <span className="text-sm text-[var(--color-text-primary)]">Group links by tag</span>
        </label>
      </div>

      {/* Theme */}
      <div>
        <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">Theme</h4>
        <p className="text-sm text-[var(--color-text-tertiary)] mb-4">
          Pick a designed background and accent pairing.
        </p>
        <div className="grid grid-cols-3 gap-2">
          {THEME_PRESETS.map((preset) => {
            const active = isPresetActive(preset);
            const accent = deriveAccent(preset.background, preset.palette);
            return (
              <button
                key={preset.palette}
                type="button"
                aria-pressed={active}
                onClick={() => applyPreset(preset)}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-medium border transition-all
                  ${
                    active
                      ? "border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]"
                      : "border-[var(--color-border)] hover:border-[var(--color-border-hover)]"
                  }`}
              >
                <span
                  className="relative w-5 h-5 rounded-full border border-[var(--color-border)] shrink-0"
                  style={{ backgroundColor: preset.background }}
                >
                  <span
                    className="absolute inset-0 m-auto w-2 h-2 rounded-full"
                    style={{ backgroundColor: accent }}
                  />
                </span>
                <span className="text-[var(--color-text-primary)]">{preset.label}</span>
              </button>
            );
          })}
        </div>
        {!THEME_PRESETS.some(isPresetActive) && (
          <p className="mt-2 text-sm text-[var(--color-text-tertiary)]">
            Custom: your background or accent isn't one of these presets.
          </p>
        )}
      </div>

      {/* Background color */}
      <div>
        <h4 className="font-semibold text-[var(--color-text-primary)] mb-2">Background</h4>

        <button
          type="button"
          onClick={() => setCustomOpen((open) => !open)}
          aria-expanded={customOpen}
          className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors mb-3"
        >
          <svg
            width="12"
            height="12"
            fill="none"
            viewBox="0 0 20 20"
            className={`transition-transform ${customOpen ? "rotate-90" : ""}`}
          >
            <path d="M7 5l6 5-6 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Custom…
        </button>

        {customOpen && (
          <>
            <div
              className="grid gap-0.5 rounded-md p-1.5 bg-[var(--color-bg)] border border-[var(--color-border)]"
              style={{ gridTemplateColumns: "repeat(11, 1fr)" }}
            >
              {COLOR_PRESETS.map((color, i) => (
                <button
                  key={i}
                  type="button"
                  className={`w-full aspect-square rounded-sm transition-transform hover:scale-150 hover:z-10
                    ${color === baseColor ? "ring-1 ring-[var(--color-accent)] scale-150 z-10" : ""}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setBaseColor(color)}
                  aria-label={`Set background to ${color}`}
                />
              ))}
            </div>

            {/* Cinematic dark presets */}
            <div className="mt-3">
              <p className="text-xs text-[var(--color-text-tertiary)] mb-2">Cinematic</p>
              <div className="flex gap-1.5">
                {CINEMATIC_PRESETS.map((preset, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setBaseColor(preset.color)}
                    className={`flex-1 h-8 rounded-md transition-all border
                      ${
                        baseColor === preset.color
                          ? "ring-1 ring-[var(--color-accent)] scale-105 border-[var(--color-accent)]"
                          : "border-[var(--color-border)] hover:scale-105"
                      }`}
                    style={{ background: preset.gradient }}
                    aria-label={`Set background to ${preset.label}`}
                    title={preset.label}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex items-center gap-2 mt-3">
          <label className="relative flex-1">
            <input
              type="color"
              value={baseColor}
              onChange={(e) => setBaseColor(e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              aria-label="Custom background color"
            />
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-xs text-[var(--color-text-secondary)] cursor-pointer hover:border-[var(--color-border-hover)]">
              <div
                className="w-3.5 h-3.5 rounded-sm border border-[var(--color-border)]"
                style={{ backgroundColor: baseColor }}
              />
              {baseColor}
            </div>
          </label>
          <button
            type="button"
            onClick={() => setBaseColor(DEFAULT_LIGHT_BG)}
            className="px-2.5 py-1.5 text-xs rounded-md border border-[var(--color-border)] text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            Light
          </button>
          <button
            type="button"
            onClick={() => setBaseColor(DEFAULT_DARK_BG)}
            className="px-2.5 py-1.5 text-xs rounded-md border border-[var(--color-border)] text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            Dark
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppearanceTab;
